import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState(slug || 'movie');

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const currentCategory = categories.find((c) => c.slug === activeTab);

  const filteredMovies = query
    ? movies.filter((m) => m.title.toLowerCase().includes(query.toLowerCase()))
    : movies.filter((m) => m.category === activeTab);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: currentCategory?.name || '全部分类' },
  ];

  if (query) {
    breadcrumbItems.splice(1, 0, { label: `搜索: ${query}` });
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="site-container">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              {query ? `搜索"${query}"的结果` : currentCategory?.name || '全部分类'}
            </h1>
            {currentCategory && !query && (
              <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveTab(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === cat.slug && !query
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-background-100 py-8 md:py-10">
          <div className="site-container">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} {...movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">
                  {query ? `未找到与"${query}"相关的内容` : '该分类暂无内容'}
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${currentCategory?.name || '全部分类'} - 蚂蚁影视`,
            description: currentCategory?.description || '蚂蚁影视高清免费在线观看',
            isPartOf: {
              "@type": "WebSite",
              name: "蚂蚁影视",
              url: "https://www.mantfilm.com"
            }
          }, null, 2)
        }}
      />
    </>
  );
}